/**
 * Model-id plumbing shared by the renderer and the content tests. Pure data
 * and functions (no three.js), so Vitest can check every content id against
 * `public/assets/models/manifest.json` without a DOM.
 *
 * Content refers to models by id (`ClassDef.model`, `MonsterDef.model`,
 * `NpcDef.model`, `PropInstance.model`). An id resolves to itself when the
 * manifest lists it, to a procedural model built in code (`fx_*`), or through
 * the alias chain below. Add an alias here instead of editing content when a
 * definition names a model that is not built.
 */

/** Fallback chains for ids that are not (or no longer) built. */
export const MODEL_ALIASES: Readonly<Record<string, readonly string[]>> = {
  // Actor default look before content assigns one.
  hero: ['hero_warrior'],
  // Prototype-era prop ids.
  tree: ['tree_oak'],
  lantern_post: ['lamp_post'],
  pillar: ['pillar_intact'],
  rock: ['rock_a'],
  chest: ['chest_closed'],
  ship: ['ship_player'],
  // Humanoid stand-ins for creatures without a model of their own.
  mob_goblin: ['mob_grubling'],
  mob_cultist: ['mob_rift_caster'],
  mob_spider: ['mob_scorpion'],
  npc: ['npc_villager_a'],
};

/**
 * Models generated in code by `AssetLibrary` (boss-mechanic helpers that are
 * effects rather than creatures): glowing orbs, fused-glass husks, prison cages.
 */
export const PROCEDURAL_MODELS: readonly string[] = ['fx_orb', 'fx_glass_husk', 'fx_cage'];

export function isProceduralModel(id: string): boolean {
  return PROCEDURAL_MODELS.includes(id);
}

/** Resolves `id` to a built or procedural model id; null when nothing matches. */
export function resolveModelId(id: string, has: (id: string) => boolean, seen: Set<string> = new Set()): string | null {
  if (has(id) || isProceduralModel(id)) return id;
  if (seen.has(id)) return null;
  seen.add(id);
  for (const alt of MODEL_ALIASES[id] ?? []) {
    const r = resolveModelId(alt, has, seen);
    if (r) return r;
  }
  return null;
}

// ------------------------------------------------------------------ gear

/** Gear models (`public/assets/models/gear`) for a weapon kind: main hand and off hand. */
export interface GearSet {
  main: string | null;
  off: string | null;
}

/**
 * Weapon kinds (`ClassDef.weapon`, `actor.look.weapon`) → gear models.
 * Paired kinds (pistols, gauntlets, daggers) go on both hands.
 */
export const WEAPON_GEAR: Readonly<Record<string, GearSet>> = {
  greatsword: { main: 'greatsword', off: null },
  staff: { main: 'staff', off: null },
  bellstaff: { main: 'bellstaff', off: null },
  pistol: { main: 'pistol', off: 'pistol' },
  shotgun: { main: 'shotgun', off: null },
  rifle: { main: 'rifle', off: null },
  gauntlet: { main: 'gauntlet', off: 'gauntlet' },
  dagger: { main: 'dagger', off: 'dagger' },
};

/** Identity modes that change the weapon in hand (Arsenalist stances). */
export const MODE_WEAPON: Readonly<Record<string, string>> = {
  sidearms: 'pistol',
  scattergun: 'shotgun',
  longrifle: 'rifle',
};

/** Models that never carry gear (transformations with natural weapons). */
export const GEARLESS_MODELS: ReadonlySet<string> = new Set(['hero_fiend']);

/** Life-skill tools, held on the `weapon` socket by the Gather and Fish clips. */
export const TOOLS = ['pickaxe', 'axe', 'sickle', 'shovel', 'fishing_rod'] as const;
export type ToolId = (typeof TOOLS)[number];

/** The tool matching a world interactable (gather node / dig site / fishing spot); null when none fits. */
export function toolFor(kind: string, node?: unknown): ToolId | null {
  if (kind === 'fish') return 'fishing_rod';
  if (kind === 'dig') return 'shovel';
  if (kind !== 'gather') return null;
  if (node === 'ore') return 'pickaxe';
  if (node === 'timber') return 'axe';
  return 'sickle';
}

/** Tool shown by default while a clip plays without an explicit `setTool`. */
export const CLIP_TOOL: Readonly<Record<string, ToolId>> = {
  Fish: 'fishing_rod',
  Gather: 'sickle',
};

// ------------------------------------------------------------------ bosses

/**
 * Breakable boss parts: model id → part id (`MonsterDef.parts[].id`) → mesh
 * node names hidden once that part breaks (listed under `breakable` in the
 * manifest).
 */
export const BREAKABLE_MESHES: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  boss_behemoth: { shell: ['boss_behemoth_shell'] },
};

/** Largest presentation up-scale applied to fit a boss model to its hit radius. */
export const BOSS_FIT_MAX = 1.8;

/**
 * Presentation scale for bosses whose model is smaller than their simulated
 * body: grows the model so its footprint matches the hit radius (never
 * shrinks, capped at `BOSS_FIT_MAX`). Returns 1 for everything else.
 */
export function bossFitScale(grade: string | undefined, simRadius: number, modelRadius: number | undefined, defScale: number): number {
  if (grade !== 'boss' && grade !== 'raid') return 1;
  if (!modelRadius || modelRadius <= 0 || defScale <= 0) return 1;
  const fit = simRadius / (modelRadius * defScale);
  return Math.min(BOSS_FIT_MAX, Math.max(1, fit));
}
